import { Observable, Subject } from 'rxjs';
import { Injectable } from '@angular/core';
import { ActivatedRoute, Params } from '@angular/router';
import { AdviceModalComponent } from '@shared/components/advice-modal/advice-modal.component';
import { InformativeLoadingModalComponent } from '@shared/components/informative-loading-modal/informative-loading-modal.component';
import { ExpandedTextareaComponent } from '@shared/components/expanded-textarea/expanded-textarea.component';
import { NewEvaluationComponent } from '@core/components/new-evaluation/new-evaluation.component';
import { OrganizationModalComponent } from '@shared/components/organization-modal/organization-modal.component';
import { ExportModalComponent } from '@shared/components/export-modal/export-modal.component';
import { ExportFormatModalComponent } from '@shared/components/export-format-modal/export-format-modal.component';
import { ImportModalComponent } from '@shared/components/import-modal/import-modal.component';
import { ResetPasswordComponent } from '@core/components/reset-password/reset-password.component';
import { ManageConfidentialModalComponent } from '@core/components/manage-confidential-modal/manage-confidential-modal.component';
import { ShareDocumentModalComponent } from '@shared/components/share-document-modal/share-document-modal.component';
import { RenameModalComponent } from '@shared/components/rename-modal/rename-modal.component';
import { BasicDataModalComponent } from '@shared/components/basic-data-modal/basic-data-modal.component';
import { DuplicateModalComponent } from '@core/components/duplicate-modal/duplicate-modal.component';
import { HelpModalComponent } from '@core/components/help-modal/help-modal.component';
import { DraftEvaluationsComponent } from '@core/components/draft-evaluations/draft-evaluations.component';
import { DuplicateEvaluationComponent } from '@shared/components/duplicate-evaluation/duplicate-evaluation.component';
import { MigrationDataComponent } from '@shared/components/migration-data/migration-data.component';
import { PublicLinkPollComponent } from '@core/components/public-link-poll/public-link-poll.component';
import { ConfigureImageComponent } from '@core/components/configure-image/configure-image.component';
import { PublicLinkComponent } from '@core/components/public-link/public-link.component';
import { CorporativeInformationModalComponent } from '@core/components/corporative-information-modal/corporative-information-modal.component';
import { NotificationPreviewComponent } from '@core/components/notification-preview/notification-preview.component';
import { UploadDocumentsModalComponent } from '@shared/components/upload-document/upload-documents-modal/upload-documents-modal.component';
import { ReferenceDocumentComponent } from '@core/components/reference-document/reference-document.component';
import { AssociatedDocumentationComponent } from '@shared/components/associated-documentation/associated-documentation.component';
import { PersonalizedNotificationModalComponent } from '@shared/components/personalized-notification-modal/personalized-notification-modal.component';
import { TaskModalComponent } from '@shared/components/task-modal/task-modal.component';
import { NewDocumentComponent } from '@shared/components/new-document/new-document.component';
import { NewSnippetModalComponent } from '@shared/components/new-snippet-modal/new-snippet-modal.component';
import { RelatedInformationModalComponent } from '@shared/components/related-information-modal/related-information-modal.component';
import { LawInfoComponent } from '@shared/components/law-info/law-info.component';
import { ShareInstanceComponent } from '@shared/components/share-instance/share-instance.component';
import { RelationshipReasonModalComponent } from '@shared/components/relationship-reason-modal/relationship-reason-modal.component';
import { AddAlertComponent } from '@shared/components/add-alert/add-alert.component';
import { ExpirationPeriodComponent } from '@shared/components/expiration-period/expiration-period.component';
import { EstablishPatternModalComponent } from '@shared/components/establish-pattern-modal/establish-pattern-modal.component';
import { ProcessModalComponent } from '@shared/components/process-modal/process-modal.component';
import { ProcessOpsModalComponent } from '@shared/components/process-ops-modal/process-ops-modal.component';
import { ConceptInfoComponent } from '@shared/components/concept-info/concept-info.component';
import { FormulaSettingModalComponent } from '@core/components/formula-setting-modal/formula-setting-modal.component';
import { UpDocumentComponent } from '@core/components/up-document/up-document.component';
import { EvaluationShareComponent } from '@core/components/evaluation-share/evaluation-share.component';
import { GenerateDocumentComponent } from '@shared/components/generate-document/generate-document.component';
import { AttachDocumentModalComponent } from '@shared/components/attach-document-modal/attach-document-modal.component';
import { AlertDetailComponent } from '@shared/components/alert-detail/alert-detail.component';
import { RoleAssignmentModalComponent } from '@core/components/role-assignment-modal/role-assignment-modal.component';
import { SendReminderComponent } from '@core/components/send-reminder/send-reminder.component';
import { ModalMoreInfoComponent } from '@shared/components/modal-more-info/modal-more-info.component';
import { NewReportComponent } from '@core/components/new-report/new-report.component';
import { DocumentHistoricComponent } from '@core/components/document-historic/document-historic.component';
import { NewVersionComponent } from '@shared/components/new-version/new-version.component';
import { RequestTrackingComponent } from '@core/components/request-tracking/request-tracking.component';
import { DuplicateDocumentationComponent } from '@core/components/duplicate-documentation/duplicate-documentation.component';
import { UploadReportComponent } from '@core/components/upload-report/upload-report.component';
import { RoleErrorModalComponent } from '@shared/components/role-error-modal/role-error-modal.component';
import { QrModalComponent } from '@shared/components/qr-modal/qr-modal.component';
import { ExportResultReportComponent } from '@core/components/export-result-report/export-result-report.component';
import { ViewExtendedReportComponent } from '@core/components/view-extended-report/view-extended-report.component';
import { FieldAssignmentsModalComponent } from '@shared/components/field-assignments-modal/field-assignments-modal.component';
import { ProcessEditModalComponent } from '@shared/components/process-edit-modal/process-edit-modal.component';
import { StatesTaskHelpModalComponent } from '@core/components/states-task-help-modal/states-task-help-modal.component';
import { ReportExportModalComponent } from '@shared/components/report-export-modal/report-export-modal.component';
import { LinkModalComponent } from '@core/components/link-modal/link-modal.component';
import { AutomaticPostProcessModalComponent } from '@core/components/automatic-post-process-modal/automatic-post-process-modal.component';
import { BlockedEditionModalComponent } from '@core/components/blocked-edition-modal/blocked-edition-modal.component';
import { ProtectionDataModalComponent } from '@core/components/protection-data-modal/protection-data-modal.component';
import { ConcursalFormsModalComponent } from '@core/components/concursal-forms-modal/concursal-forms-modal.component';
import { ChangeStateModalComponent } from '@shared/components/change-state-modal/change-state-modal.component';
import { AssignManagerComponent } from '@shared/components/assign-manager/assign-manager.component';
import { ConsultListsModalComponent } from '@shared/components/consult-lists-modal/consult-lists-modal.component';
import { MoveModalComponent } from '@core/components/move-modal/move-modal.component';
import { FinishBlockModalComponent } from '@shared/components/finish-block-modal/finish-block-modal.component';
import { RecordAudioModalComponent } from '@shared/components/record-audio-modal/record-audio-modal.component';
import { ListenRecordComponent } from '@shared/components/listen-record/listen-record.component';
import { DialogService, DynamicDialogConfig, DynamicDialogRef } from 'primeng/dynamicdialog';
import { NormalModalComponent } from 'src/app/resources/components/normal-modal/normal-modal.component';
import { NewContactComponent } from '@shared/components/new-contact/new-contact.component';
import { FormConfigurationModalComponent } from '@core/components/form-configuration-modal/form-configuration-modal.component';
import { CustomizeFieldsModalComponent } from '@core/components/customize-fields-modal/customize-fields-modal.component';
import { PublishedConceptModalComponent } from '@core/components/published-concept-modal/published-concept-modal.component';
import { ShareLinkModalComponent } from '@shared/components/share-link-modal/share-link-modal.component';
import { ChangeStateProcessModalComponent } from '@shared/components/change-state-process-modal/change-state-process-modal.component';
import { ChangeAssociationModalComponent } from '@shared/components/change-association-modal/change-association-modal.component';
import { DateSelectorModalComponent } from '@shared/components/date-selector-modal/date-selector-modal.component';
import { FormTranslationsModalComponent } from '@core/components/form-translations-modal/form-translations-modal.component';

@Injectable({
  providedIn: 'root'
})
export class ModalServiceService {
  constructor(private route: ActivatedRoute,
    private dialogService: DialogService) {}

  dialogRefCollection: any = new Map();
  params:Params = this.route.snapshot.params;

  private baseDynamicDialog(configuration:any={}):Observable<void>{
    let specificClass: string = configuration.class?' '+configuration.class:'';
    const dialogConfig: DynamicDialogConfig = {
      data: configuration.data,
      modal: true,
      showHeader: false,
      duplicate: true,
      maskStyleClass: 'common-modal__mask',
      styleClass: 'common-modal__dynamic-dialog' + specificClass
    }
    // let dialogMapped: any = this.dialogRefCollection.get(configuration.name);
    // if(dialogMapped){
    //   dialogMapped.destroy();
    // }
    let dialogRef: DynamicDialogRef<any> = this.dialogService.open(configuration.component, dialogConfig);
    const modalSubject:Subject<void> = new Subject<void>();
    dialogRef.onClose.subscribe((result: any) =>{
      modalSubject.next(result);
      this.dialogRefCollection.delete(configuration.name);
    });
    this.dialogRefCollection.set(configuration.name, dialogRef);
    return modalSubject.asObservable();
  }

  modalExample(configuration: any={}){
    let configurationModal: any = {
      component: NormalModalComponent,
      name: 'NormalModalComponent',
      data: configuration
    }
    return this.baseDynamicDialog(configurationModal);
  }

  infoModal(configuration: any){
    // info:question:question -> {type: 'info', typeButtons: 'question'}
    configuration.type = 'info';
    configuration.typeButtons = configuration.typeButtons?configuration.typeButtons:'question';
    return this.adviceModal(configuration);
  }

  continueModal(configuration: any){
    // info:question:question -> {type: 'info', typeButtons: 'question'}
    configuration.type = 'info';
    configuration.typeButtons = configuration.typeButtons?configuration.typeButtons:'cancelContinue';
    return this.adviceModal(configuration);
  }

  successModal(configuration: any){
    // info:accept:check-round -> {type: 'success', typeButtons: 'accept'}
    configuration.type = 'success';
    configuration.typeButtons = configuration.typeButtons?configuration.typeButtons:'accept';
    return this.adviceModal(configuration);
  }

  errorModal(configuration: any){
    // error:accept:warning -> {type: 'alert', typeButtons: 'accept'}
    configuration.type = 'error';
    configuration.typeButtons = configuration.typeButtons?configuration.typeButtons:'accept';
    return this.adviceModal(configuration);
  }

  alertModal(configuration: any){
    configuration.type = 'alert';
    configuration.typeButtons = configuration.typeButtons?configuration.typeButtons:'accept';
    return this.adviceModal(configuration);
  }

  warningModal(configuration: any){
    configuration.type = 'warning';
    configuration.typeButtons = configuration.typeButtons?configuration.typeButtons:'question';
    return this.adviceModal(configuration);
  }

  adviceModal(configuration: any) {
    let configurationModal: any = {
      component: AdviceModalComponent,
      name: 'AdviceModalComponent',
      data: configuration,
      class: 'common-modal__dynamic-dialog--feedback'
    }
    return this.baseDynamicDialog(configurationModal);
  }

  informativeLoadingModal(itemSelected:any) {
    let configurationModal: any = {
      component: InformativeLoadingModalComponent,
      name: 'InformativeLoadingModalComponent',
      data: { itemSelected: itemSelected },
      class: 'common-modal__dynamic-dialog--feedback'
    }
    return this.baseDynamicDialog(configurationModal);
  }

  expandTextarea(text:string, field:any, concept:any = null, voiceRecord: any = null, acceptAceptation: boolean = false){
    let configurationModal: any = {
      component: ExpandedTextareaComponent,
      name: 'ExpandedTextareaComponent',
      data: {
        text: text,
        field: field,
        concept: concept,
        voiceRecord: voiceRecord,
        acceptAceptation: acceptAceptation
      },
      class: 'expanded-textarea__dynamic-dialog'
    }
    return this.baseDynamicDialog(configurationModal);
  }

  createNewEvaluation(configuration:any=null){
    let configurationModal: any = {
      component: NewEvaluationComponent,
      name: 'NewEvaluationComponent',
      data: configuration
    }
    return this.baseDynamicDialog(configurationModal);
  }

  organizationModal(configuration:any){
    let configurationModal: any = {
      component: OrganizationModalComponent,
      name: 'OrganizationModalComponent',
      data: { configuration: configuration }
    }
    return this.baseDynamicDialog(configurationModal);
  }

  exportModal(configuration:any) {
    let configurationModal: any = {
      component: ExportModalComponent,
      name: 'ExportModalComponent',
      data: configuration
    }
    return this.baseDynamicDialog(configurationModal);
  }

  exportFormatModal(configuration:any) {
    let configurationModal: any = {
      component: ExportFormatModalComponent,
      name: 'ExportFormatModalComponent',
      data: configuration
    }
    return this.baseDynamicDialog(configurationModal);
  }

  importModal(type:string='', concept:any=undefined) {
    let configurationModal: any = {
      component: ImportModalComponent,
      name: 'ImportModalComponent',
      data: {
        type: type,
        concept: concept
      }
    }
    return this.baseDynamicDialog(configurationModal);
  }

  migrationData(isClient:boolean=false, type:string = 'organizations'){
    let configurationModal: any = {
      component: MigrationDataComponent,
      name: 'MigrationDataComponent',
      data: { isClient: isClient, type: type }
    }
    return this.baseDynamicDialog(configurationModal);
  }

  resetPassword(){
    let configurationModal: any = {
      component: ResetPasswordComponent,
      name: 'ResetPasswordComponent',
      data: {}
    }
    return this.baseDynamicDialog(configurationModal);
  }

  manageConfidentialModal(itemToConfidential:any){
    let configurationModal: any = {
      component: ManageConfidentialModalComponent,
      name: 'ManageConfidentialModalComponent',
      data: { itemToConfidential: itemToConfidential }
    }
    return this.baseDynamicDialog(configurationModal);
  }

  shareDocument(configuration:any){
    let configurationModal: any = {
      component: ShareDocumentModalComponent,
      name: 'ShareDocumentModalComponent',
      data: configuration
    }
    return this.baseDynamicDialog(configurationModal);
  }

  renameModal(configuration:any){
    let configurationModal: any = {
      component: RenameModalComponent,
      name: 'RenameModalComponent',
      data: configuration
    }
    return this.baseDynamicDialog(configurationModal);
  }

  basicDataModal(configuration: any){
    let configurationModal: any = {
      component: BasicDataModalComponent,
      name: 'BasicDataModalComponent',
      data: { configuration }
    }
    return this.baseDynamicDialog(configurationModal);
  }

  duplicateModal(configuration:any){
    let configurationModal: any = {
      component: DuplicateModalComponent,
      name: 'DuplicateModalComponent',
      data: configuration
    }
    return this.baseDynamicDialog(configurationModal);
  }

  draftEvaluationsModal(toOther: any, draftList:any){
    let configurationModal: any = {
      component: DraftEvaluationsComponent,
      name: 'DraftEvaluationsComponent',
      data: {
        toOther: toOther,
        draftList: draftList,
      },
      class: 'common-modal__dynamic-dialog--feedback'
    }
    return this.baseDynamicDialog(configurationModal);
  }

  duplicateEvaluationModal(evaluation:any){
    let configurationModal: any = {
      component: DuplicateEvaluationComponent,
      name: 'DuplicateEvaluationComponent',
      data: { evaluation: evaluation }
    }
    return this.baseDynamicDialog(configurationModal);
  }

  configureImage(logo:any, from:any, preSelectLogos:any){
    let configurationModal: any = {
      component: ConfigureImageComponent,
      name: 'ConfigureImageComponent',
      data: {
        logo: logo,
        from: from,
        preSelectLogos: preSelectLogos
      }
    }
    return this.baseDynamicDialog(configurationModal);
  }

  publicLinkPoll(concept:any, evaluationInfo:any){
    let configurationModal: any = {
      component: PublicLinkPollComponent,
      name: 'PublicLinkPollComponent',
      data: {
        concept: concept,
        evaluationInfo: evaluationInfo
      },
      class: 'common-modal__dynamic-dialog--auto-width'
    }
    return this.baseDynamicDialog(configurationModal);
  }

  publicLink(concept:any, evaluationInfo:any){
    let configurationModal: any = {
      component: PublicLinkComponent,
      name: 'PublicLinkComponent',
      data: {
        concept: concept,
        evaluationInfo: evaluationInfo
      },
      class: 'common-modal__dynamic-dialog--auto-width'
    }
    return this.baseDynamicDialog(configurationModal);
  }

  corporativeInformation(concept:any, clientId:any, corporativeInformation:any){
    let configurationModal: any = {
      component: CorporativeInformationModalComponent,
      name: 'CorporativeInformationModalComponent',
      data: {
        concept: concept,
        clientId: clientId,
        corporativeInformation: corporativeInformation
      }
    }
    return this.baseDynamicDialog(configurationModal);
  }

  notificationPreview(configuration:any){
    let configurationModal: any = {
      component: NotificationPreviewComponent,
      name: 'NotificationPreviewComponent',
      data: configuration,
      class: 'notification-preview__dynamic-dialog'
    }
    return this.baseDynamicDialog(configurationModal);
  }

  uploadDocuments(type:any, files:any, documentationList:any, itemSelected:any, listSelect:any=[]){
    let configurationModal: any = {
      component: UploadDocumentsModalComponent,
      name: 'UploadDocumentsModalComponent',
      data: {
        type: type,
        files: files,
        documentationList: documentationList,
        itemSelected: itemSelected,
        listSelect: listSelect,
      }
    }
    return this.baseDynamicDialog(configurationModal);
  }

  referenceDocument(configuration:any){
    let configurationModal: any = {
      component: ReferenceDocumentComponent,
      name: 'ReferenceDocumentComponent',
      data: configuration
    }
    return this.baseDynamicDialog(configurationModal);
  }

  processModal(configuration: any){
    let configurationModal: any = {
      component: ProcessModalComponent,
      name: 'ProcessModalComponent',
      data: configuration
    }
    return this.baseDynamicDialog(configurationModal);
  }

  processOpsModal(evaluationInfo:any, concept:any, from:any){
    let configurationModal: any = {
      component: ProcessOpsModalComponent,
      name: 'ProcessOpsModalComponent',
      data: {
        evaluationInfo: evaluationInfo,
        concept: concept,
        from: from
      }
    }
    return this.baseDynamicDialog(configurationModal);
  }

  associatedDocumentation(process:any, concept:any, listSelect:any, from:any, document:any=null){
    let configurationModal: any = {
      component: AssociatedDocumentationComponent,
      name: 'AssociatedDocumentationComponent',
      data: {
        process: process,
        concept: concept,
        from: from,
        listSelect: listSelect,
        document: document
      }
    }
    return this.baseDynamicDialog(configurationModal);
  }

  personzalizedNotificationModal(concept:any, state:any){
    let configurationModal: any = {
      component: PersonalizedNotificationModalComponent,
      name: 'PersonalizedNotificationModalComponent',
      data: {
        state: state,
        concept: concept,
      }
    }
    return this.baseDynamicDialog(configurationModal);
  }

  taskModal(originalTask:any, concept: any){
    let configurationModal: any = {
      component: TaskModalComponent,
      name: 'TaskModalComponent',
      data: {
        originalTask: originalTask,
        concept: concept,
      }
    }
    return this.baseDynamicDialog(configurationModal);
  }

  newDocument(configuration:any){
    let configurationModal: any = {
      component: NewDocumentComponent,
      name: 'NewDocumentComponent',
      data: configuration
    }
    return this.baseDynamicDialog(configurationModal);
  }

  newSnippetModal(list:any, configuration:any){
    let configurationModal: any = {
      component: NewSnippetModalComponent,
      name: 'NewSnippetModalComponent',
      data: {
        list: list,
        configuration: configuration
      }
    }
    return this.baseDynamicDialog(configurationModal);
  }

  relatedInformationModal(infoRelated: any){
    let configurationModal: any = {
      component: RelatedInformationModalComponent,
      name: 'RelatedInformationModalComponent',
      data: { infoRelated: infoRelated }
    }
    return this.baseDynamicDialog(configurationModal);
  }

  lawInfoModal(registerEvent:any={}, references:any=[], selected:any= null){
    let configurationModal: any = {
      component: LawInfoComponent,
      name: 'LawInfoComponent',
      data: {
        registerEvent: registerEvent,
        references: references,
        selected: selected
      }
    }
    return this.baseDynamicDialog(configurationModal);
  }

  shareInstance(configuration: any){
    let configurationModal: any = {
      component: ShareInstanceComponent,
      name: 'ShareInstanceComponent',
      data: configuration
    }
    return this.baseDynamicDialog(configurationModal);
  }

  relationshipReasonModal(text:any){
    let configurationModal: any = {
      component: RelationshipReasonModalComponent,
      name: 'RelationshipReasonModalComponent',
      data: { text: text }
    }
    return this.baseDynamicDialog(configurationModal);
  }

  addAlert(configuration:any){
    let configurationModal: any = {
      component: AddAlertComponent,
      name: 'AddAlertComponent',
      data: configuration
    }
    return this.baseDynamicDialog(configurationModal);
  }

  expirationPeriod(concept:any){
    let configurationModal: any = {
      component: ExpirationPeriodComponent,
      name: 'ExpirationPeriodComponent',
      data: { concept: concept }
    }
    return this.baseDynamicDialog(configurationModal);
  }

  establishPattern(configuration: any){
    let configurationModal: any = {
      component: EstablishPatternModalComponent,
      name: 'EstablishPatternModalComponent',
      data: configuration
    }
    return this.baseDynamicDialog(configurationModal);
  }

  conceptInfo(concept:any){
    let configurationModal: any = {
      component: ConceptInfoComponent,
      name: 'ConceptInfoComponent',
      data: { concept: concept }
    }
    return this.baseDynamicDialog(configurationModal);
  }

  helpModal(){
    let configurationModal: any = {
      component: HelpModalComponent,
      name: 'HelpModalComponent'
    }
    return this.baseDynamicDialog(configurationModal);
  }

  modalFormulaSettings(configurationField:any){
    let configurationModal: any = {
      component: FormulaSettingModalComponent,
      name: 'FormulaSettingModalComponent',
      data: configurationField,
      class: 'common-modal__dynamic-dialog--big-size',
    }
    return this.baseDynamicDialog(configurationModal);
  }

  upDocument(type:any){
    let configurationModal: any = {
      component: UpDocumentComponent,
      name: 'UpDocumentComponent',
      data: { type: type }
    }
    return this.baseDynamicDialog(configurationModal);
  }

  evaluationShare(evaluation:any){
    let configurationModal: any = {
      component: EvaluationShareComponent,
      name: 'EvaluationShareComponent',
      data: { evaluation: evaluation }
    }
    return this.baseDynamicDialog(configurationModal);
  }

  finishBlockModal(configuration:any){
    let configurationModal: any = {
      component: FinishBlockModalComponent,
      name: 'FinishBlockModalComponent',
      data: configuration
    }
    return this.baseDynamicDialog(configurationModal);
  }

  generateDocument(predefinedTemplate:any, concept:any){
    let configurationModal: any = {
      component: GenerateDocumentComponent,
      name: 'GenerateDocumentComponent',
      data: {
        concept: concept,
        predefinedTemplate: predefinedTemplate
      }
    }
    return this.baseDynamicDialog(configurationModal);
  }

  attachDocumentModal(item:any){
    let configurationModal: any = {
      component: AttachDocumentModalComponent,
      name: 'AttachDocumentModalComponent',
      data: { item: item }
    }
    return this.baseDynamicDialog(configurationModal);
  }

  alertDetail(type:any, alert:any){
    let configurationModal: any = {
      component: AlertDetailComponent,
      name: 'AlertDetailComponent',
      data: {
        type: type,
        alert: alert
      }
    }
    return this.baseDynamicDialog(configurationModal);
  }

  roleAssignmentModal(configuration:any){
    let configurationModal: any = {
      component: RoleAssignmentModalComponent,
      name: 'RoleAssignmentModalComponent',
      data: configuration,
    }
    return this.baseDynamicDialog(configurationModal);
  }

  sendReminder(task:any){
    let configurationModal: any = {
      component: SendReminderComponent,
      name: 'SendReminderComponent',
      data: {task: task}
    }
    return this.baseDynamicDialog(configurationModal);
  }

  modalMoreInfo(configuration:any){
    let configurationModal: any = {
      component: ModalMoreInfoComponent,
      name: 'ModalMoreInfoComponent',
      data: configuration
    }
    return this.baseDynamicDialog(configurationModal);
  }

  createNewReport(evaluationList:any, filteredProduct: any){
    let configurationModal: any = {
      component: NewReportComponent,
      name: 'NewReportComponent',
      data: {
        evaluationList: evaluationList,
        filteredProduct: filteredProduct
      }
    }
    return this.baseDynamicDialog(configurationModal);
  }

  newVersion(){
    let configurationModal: any = {
      component: NewVersionComponent,
      name: 'NewVersionComponent'
    };
    return this.baseDynamicDialog(configurationModal);
  }

  documentHistoric(document:any){
    let configurationModal: any = {
      component: DocumentHistoricComponent,
      name: 'DocumentHistoricComponent',
      data: { document: document }
    }
    return this.baseDynamicDialog(configurationModal);
  }

  requestTrackingModal(document:any){
    let configurationModal: any = {
      component: RequestTrackingComponent,
      name: 'RequestTrackingComponent',
      data: { document: document }
    }
    return this.baseDynamicDialog(configurationModal);
  }

  moveModal(configuration:any){
    let configurationModal: any = {
      component: MoveModalComponent,
      name: 'MoveModalComponent',
      data: configuration
    }
    return this.baseDynamicDialog(configurationModal);
  }

  duplicateDocumentation(concept:any, listOriginDocuments:any, folderId:any){
    let configurationModal: any = {
      component: DuplicateDocumentationComponent,
      name: 'DuplicateDocumentationComponent',
      data: {
        concept: concept,
        listOriginDocuments: listOriginDocuments,
        folderId: folderId
      }
    }
    return this.baseDynamicDialog(configurationModal);
  }

  uploadReport(reportVersions:any){
    let configurationModal: any = {
      component: UploadReportComponent,
      name: 'UploadReportComponent',
      data: { reportVersions: reportVersions }
    }
    return this.baseDynamicDialog(configurationModal);
  }

  roleErrorModal(configuration:any){
    let configurationModal: any = {
      component: RoleErrorModalComponent,
      name: 'RoleErrorModalComponent',
      data: configuration,
      class: 'common-modal__dynamic-dialog--feedback'
    }
    return this.baseDynamicDialog(configurationModal);
  }

  qrModal(url:any){
    let configurationModal: any = {
      component: QrModalComponent,
      name: 'QrModalComponent',
      data: { url: url },
      class: 'qr-modal__dynamic-dialog'
    }
    return this.baseDynamicDialog(configurationModal);
  }

  exportResultReport(exportFieldsList: any, concept: any){
    let configurationModal: any = {
      component: ExportResultReportComponent,
      name: 'ExportResultReportComponent',
      data: {
        exportFieldsList: exportFieldsList,
        concept: concept
      }
    }
    return this.baseDynamicDialog(configurationModal);
  }

  viewExtendedReport(data:any){
    let configurationModal: any = {
      component: ViewExtendedReportComponent,
      name: 'ViewExtendedReportComponent',
      data: data,
      class: 'common-modal__dynamic-dialog--window-scroll'
    }
    return this.baseDynamicDialog(configurationModal);
  }

  fieldAssignments(data:any) {
    let configurationModal: any = {
      component: FieldAssignmentsModalComponent,
      name: 'FieldAssignmentsModalComponent',
      data: data
    }
    return this.baseDynamicDialog(configurationModal);
  }

  processEdit(evaluationId:any, concept:any) {
    let configurationModal: any = {
      component: ProcessEditModalComponent,
      name: 'ProcessEditModalComponent',
      data: {
        evaluationId: evaluationId,
        concept: concept
      }
    }
    return this.baseDynamicDialog(configurationModal);
  }

  statesTaskHelp(){
    let configurationModal: any = {
      component: StatesTaskHelpModalComponent,
      name: 'StatesTaskHelpModalComponent'
    };
    return this.baseDynamicDialog(configurationModal);
  }

  reportExport(typeReport:any){
    let configurationModal: any = {
      component: ReportExportModalComponent,
      name: 'ReportExportModalComponent',
      data: { typeReport: typeReport }
    }
    return this.baseDynamicDialog(configurationModal);
  }

  linkModal(node:any, selectedContent:any){
    let configurationModal: any = {
      component: LinkModalComponent,
      name: 'LinkModalComponent',
      data: {
        node: node,
        selectedContent: selectedContent
      }
    }
    return this.baseDynamicDialog(configurationModal);
  }

  automaticPostProcess(concept:any, item:any){
    let configurationModal: any = {
      component: AutomaticPostProcessModalComponent,
      name: 'AutomaticPostProcessModalComponent',
      data: {
        concept: concept,
        item: item
      }
    }
    return this.baseDynamicDialog(configurationModal);
  }

  blockedEditionModal(concept:any, processStatesSelected:any){
    let configurationModal: any = {
      component: BlockedEditionModalComponent,
      name: 'BlockedEditionModalComponent',
      data: {
        concept: concept,
        processStatesSelected: processStatesSelected
      }
    }
    return this.baseDynamicDialog(configurationModal);
  }

  protectionDataModal(configuration:any){
    let configurationModal: any = {
      component: ProtectionDataModalComponent,
      name: 'ProtectionDataModalComponent',
      data: configuration
    }
    return this.baseDynamicDialog(configurationModal);
  }

  concursalForms(configuration:any) {
    let configurationModal: any = {
      component: ConcursalFormsModalComponent,
      name: 'ConcursalFormsModalComponent',
      data: configuration
    }
    return this.baseDynamicDialog(configurationModal);
  }

  changeState(evaluateConcept:any){
    let configurationModal: any = {
      component: ChangeStateModalComponent,
      name: 'ChangeStateModalComponent',
      data: {evaluateConcept: evaluateConcept}
    }
    return this.baseDynamicDialog(configurationModal);
  }

  assignManager(count:any){
    let configurationModal: any = {
      component: AssignManagerComponent,
      name: 'AssignManagerComponent',
      data: {count: count}
    }
    return this.baseDynamicDialog(configurationModal);
  }

  consultListsModal(configuration:any){
    let configurationModal: any = {
      component: ConsultListsModalComponent,
      name: 'ConsultListsModalComponent',
      data: configuration
    }
    return this.baseDynamicDialog(configurationModal);
  }

  recordAudioModal(concept:any){
    let configurationModal: any = {
      component: RecordAudioModalComponent,
      name: 'RecordAudioModalComponent',
      data: {concept: concept},
      class: 'common-modal__dynamic-dialog--audio-modal'
    }
    return this.baseDynamicDialog(configurationModal);
  }

  listenRecord(voiceRecord:any){
    let configurationModal: any = {
      component: ListenRecordComponent,
      name: 'ListenRecordComponent',
      class:'common-modal__dynamic-dialog--listen-record-modal',
      data: {voiceRecord: voiceRecord}
    }
    return this.baseDynamicDialog(configurationModal);
  }

  newContact(configuration: any={}){
    let configurationModal: any = {
      component: NewContactComponent,
      name: 'NewContactComponent',
      data: configuration
    }
    return this.baseDynamicDialog(configurationModal);
  }

  formConfigurationModal(configuration:any){
    let configurationModal: any = {
      component: FormConfigurationModalComponent,
      name: 'FormConfigurationModalComponent',
      data: configuration
    }
    return this.baseDynamicDialog(configurationModal);
  }

  customizeFieldModal(configuration:any){
    let configurationModal: any = {
      component: CustomizeFieldsModalComponent,
      name: 'CustomizeFieldsModalComponent',
      data: configuration
    }
    return this.baseDynamicDialog(configurationModal);
  }

  publishedConceptModal(configuration:any){
    let configurationModal: any = {
      component: PublishedConceptModalComponent,
      name: 'PublishedConceptModalComponent',
      data: configuration,
      class: 'common-modal__dynamic-dialog--feedback'
    }
    return this.baseDynamicDialog(configurationModal);
  }

  shareLinkModal(configuration:any){
    let configurationModal: any = {
      component: ShareLinkModalComponent,
      name: 'ShareLinkModalComponent',
      data: configuration,
    }
    return this.baseDynamicDialog(configurationModal);
  }

  changeStateProcessModal(configuration:any){
    let configurationModal: any = {
      component: ChangeStateProcessModalComponent,
      name: 'ChangeStateProcessModalComponent',
      data: configuration
    }
    return this.baseDynamicDialog(configurationModal);
  }

  changeAssociation(configuration:any){
    let configurationModal: any = {
      component: ChangeAssociationModalComponent,
      name: 'ChangeAssociationModalComponent',
      data: configuration
    }
    return this.baseDynamicDialog(configurationModal);
  }

  dateSelectorModal(configuration:any){
    let configurationModal: any = {
      component: DateSelectorModalComponent,
      name: 'DateSelectorModalComponent',
      data: configuration,
      class: 'date-selector-modal__dynamic-dialog'
    }
    return this.baseDynamicDialog(configurationModal);
  }

  formTranslations(configuration: any) {
    let configurationModal: any = {
      component: FormTranslationsModalComponent,
      name: 'FormTranslationsModalComponent',
      data: configuration
    }
    return this.baseDynamicDialog(configurationModal);
  }

  close(){
    this.dialogRefCollection.forEach((value: any, key: any)=>{
      this.dialogRefCollection.get(key).destroy();
    });
    this.dialogRefCollection = new Map();
  }
}
